import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Truck } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { useSeo, absImage, graph, crumbs, productSchema } from "@/lib/seo";
import { ProductGallery } from "@/components/shop/ProductGallery";
import { PriceBlock, StockBadge } from "@/components/shop/ProductBits";
import { ProductActions } from "@/components/shop/ProductActions";
import { ProductDetails } from "@/components/shop/ProductDetails";
import NotFound from "./NotFound";

export default function Product() {
  const { slug } = useParams();
  const { settings } = useSite();
  const [p, setP] = useState(undefined);
  useEffect(() => {
    setP(undefined);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [slug, setP]);
  useSeo({
    title: p ? `${p.name} (${p.pack_size}) – ${p.subtitle} | Young Spine Tricity` : "Shop | Young Spine Tricity",
    description: p ? `${p.name} – ${p.subtitle}. ${p.product_type}, ${p.pack_size}, MRP ₹${p.mrp}. From Young Spine Tricity, Mohali, with delivery across India.` : "",
    path: `/shop/${slug}`,
    image: p?.images?.[0]?.url?.startsWith("/images") ? p.images[0].url.replace(".webp", ".jpg") : undefined,
    jsonLd: p ? graph(productSchema(p, settings, (p.images || []).map((i) => absImage(i.url))), crumbs(["Shop", "/shop"], [p.name, `/shop/${p.slug}`])) : null,
  });
  if (p === null) return <NotFound />;
  if (p === undefined) return <div className="container-x py-20"><div className="h-[28rem] animate-pulse rounded-3xl bg-brand-tint" /></div>;
  return (
    <div className="pb-16 md:pb-24">
      <nav aria-label="Breadcrumb" className="container-x pt-6">
        <ol className="flex items-center gap-1 text-sm text-slate-600">
          <li><Link to="/shop" className="hover:text-brand-blue">Shop</Link></li>
          <li aria-hidden="true"><ChevronRight className="h-4 w-4" /></li>
          <li aria-current="page" className="font-medium text-brand-ink">{p.name}</li>
        </ol>
      </nav>
      <div className="container-x grid gap-10 pt-6 lg:grid-cols-2 lg:gap-16">
        <ProductGallery images={p.images} name={p.name} />
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="eyebrow">{p.category}</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-brand-ink sm:text-5xl" data-testid="product-name">{p.name}</h1>
          <p className="mt-2 text-lg font-medium text-brand-greenDeep" data-testid="product-subtitle">{p.subtitle}</p>
          <p className="mt-1 text-sm text-slate-600">{p.product_type} · {p.pack_size}</p>
          <div className="mt-6"><PriceBlock product={p} /></div>
          <div className="mt-4"><StockBadge product={p} /></div>
          <div className="mt-6"><ProductActions product={p} testid="product" /></div>
          <p className="mt-6 flex items-start gap-2 text-sm text-slate-600" data-testid="product-delivery-text">
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-brand-greenDark" aria-hidden="true" /> {settings.delivery_text} Delivery within India only.
          </p>
        </div>
      </div>
      <div className="container-x mt-12"><ProductDetails product={p} /></div>
    </div>
  );
}
